export type AnalyticsEvent = [string, Record<string, any>]

export const ANALYTICS_EVENTS = {
  // iap
  iapShow: (source?: ProFeatureIdentifier): AnalyticsEvent => ['iap_show', { source }],
  iapCancel: (): AnalyticsEvent => ['iap_cancel', {}],
  iapOpenTerms: (): AnalyticsEvent => ['iap_open_terms', {}],
  iapOpenPrivacyPolicy: (): AnalyticsEvent => ['iap_open_privacy_policy', {}],
  iapPurchaseStart: (source: ProFeatureIdentifier): AnalyticsEvent => [
    'iap_purchase_start',
    { source }
  ],
  iapPurchaseSuccess: (source: ProFeatureIdentifier): AnalyticsEvent => [
    'iap_purchase_success',
    { source }
  ],
  iapPurchaseCancel: (product: string, source?: string): AnalyticsEvent => [
    'iap_purchase_cancel',
    { product, source }
  ],
  iapPurchaseDefer: (product: string): AnalyticsEvent => ['iap_purchase_defer', { product }],
  iapPurchaseFail: (product: string, error: string): AnalyticsEvent => [
    'iap_purchase_fail',
    { product, error }
  ],
  iapRestoreStart: (): AnalyticsEvent => ['iap_restore_start', {}],
  iapRestoreSuccess: (): AnalyticsEvent => ['iap_restore_success', {}],
  iapRestoreFail: (): AnalyticsEvent => ['iap_restore_fail', {}],

  // tracking
  trackingStart: (): AnalyticsEvent => ['tracking_start', {}],
  trackingFinish: (
    batteryPerHour: number,
    distance: number,
    duration: number,
    withNavigation: boolean,
    withPackride: boolean
  ): AnalyticsEvent => [
    'tracking_finish',
    {
      battery_per_hour: batteryPerHour,
      distance,
      duration,
      with_navigation: withNavigation,
      with_packride: withPackride
    }
  ],
  trackingPause: (isAutoPause: boolean): AnalyticsEvent => [
    'tracking_pause',
    { is_auto_pause: isAutoPause }
  ],
  trackingContinue: (): AnalyticsEvent => ['tracking_continue', {}],
  trackingPhotoAdd: (amount: number): AnalyticsEvent => ['tracking_photo_add', { amount }],
  trackingBatteryManagerProblem: (): AnalyticsEvent => ['tracking_battery_manager_problem', {}],
  trackingCancel: (): AnalyticsEvent => ['tracking_cancel', {}],

  // account
  accountLoginShow: (): AnalyticsEvent => ['account_login_show', {}],
  accountLoginSuccess: (type: 'email' | 'google' | 'facebook' | 'apple'): AnalyticsEvent => [
    'account_login_success',
    { type }
  ],
  accountLoginCancel: (): AnalyticsEvent => ['account_login_cancel', {}],
  accountRegisterShow: (): AnalyticsEvent => ['account_register_show', {}],
  accountRegisterCancel: (): AnalyticsEvent => ['account_register_cancel', {}],
  accountRegisterSuccess: (type: 'email' | 'google' | 'facebook' | 'apple'): AnalyticsEvent => [
    'account_register_success',
    { type }
  ],
  accountRegisterNewsletter: (
    allowNewsletter: boolean,
    allowPushNotifications: boolean
  ): AnalyticsEvent => [
    'account_register_newsletter',
    { allow_newsletter: allowNewsletter, allow_push_notifications: allowPushNotifications }
  ],
  accountRegisterNewsletterSkip: (): AnalyticsEvent => ['account_register_newsletter_skip', {}],
  accountRegisterInterests: (interests: string[]): AnalyticsEvent => [
    'account_register_interests',
    { interests }
  ],
  accountRegisterInterestsSkip: (): AnalyticsEvent => ['account_register_interests_skip', {}],
  accountRegisterAddBikeSkip: (): AnalyticsEvent => ['account_register_add_bike_skip', {}],
  accountRegisterGoalSkip: (): AnalyticsEvent => ['account_register_goal_skip', {}],
  accountLogout: (): AnalyticsEvent => ['account_logout', {}],
  accountDelete: (): AnalyticsEvent => ['account_delete', {}],
  accountForgotPassword: (): AnalyticsEvent => ['account_forgot_password', {}],
  accountInfoUpdate: (): AnalyticsEvent => ['account_info_update', {}],

  // trip
  tripDetailShow: (): AnalyticsEvent => ['trip_detail_show', {}],
  tripDelete: (): AnalyticsEvent => ['trip_delete', {}],
  tripShareShow: (source: string): AnalyticsEvent => ['trip_share_show', { source }],
  tripShareCancel: (): AnalyticsEvent => ['trip_share_cancel', {}],
  tripShareSuccess: (platform: string, contentType: string): AnalyticsEvent => [
    'trip_share_success',
    { platform, content_type: contentType }
  ],
  tripEditShow: (): AnalyticsEvent => ['trip_edit_show', {}],
  tripEditCancel: (): AnalyticsEvent => ['trip_edit_cancel', {}],
  tripEditSave: (): AnalyticsEvent => ['trip_edit_save', {}],
  tripComment: (source: string): AnalyticsEvent => ['trip_comment', { source }],
  tripLike: (source: string): AnalyticsEvent => ['trip_like', { source }],
  tripRewindShow: (): AnalyticsEvent => ['trip_rewind_show', {}],
  tripRewindShareShow: (): AnalyticsEvent => ['trip_rewind_share_show', {}],
  tripRewindShareSuccess: (): AnalyticsEvent => ['trip_rewind_share_success', {}],
  tripRewindSaveSuccess: (): AnalyticsEvent => ['trip_rewind_save_success', {}],
  tripRewindSettingShow: (): AnalyticsEvent => ['trip_rewind_setting_show', {}],
  tripRewindCreate: (params: {
    orientation: string
    lineColor: string
    unit: string | undefined
  }): AnalyticsEvent => ['trip_rewind_create', params],
  tripPhotoLike: (): AnalyticsEvent => ['trip_photo_like', {}],
  tripPhotoAiCreate: (unit: string): AnalyticsEvent => ['trip_photo_ai_create', { unit }],

  // garage
  garageShow: (): AnalyticsEvent => ['garage_show', {}],
  garageBikeShow: (): AnalyticsEvent => ['garage_bike_show', {}],
  garageBikeAddStart: (): AnalyticsEvent => ['garage_bike_add_start', {}],
  garageBikeAddSuccess: (brand: string, model: string): AnalyticsEvent => [
    'garage_bike_add_success',
    { brand, model }
  ],
  garageBikeSuggestionSubmit: (): AnalyticsEvent => ['garage_bike_suggestion_submit', {}],
  garageBikeEditShow: (): AnalyticsEvent => ['garage_bike_edit_show', {}],
  garageBikeEditSuccess: (): AnalyticsEvent => ['garage_bike_edit_success', {}],
  garageBikeDelete: (): AnalyticsEvent => ['garage_bike_delete', {}],
  garageBikeMarkSold: (): AnalyticsEvent => ['garage_bike_mark_sold', {}],
  garageBikeRevokeSold: (): AnalyticsEvent => ['garage_bike_revoke_sold', {}],
  garageBikeServiceDone: (): AnalyticsEvent => ['garage_bike_service_done', {}],
  garageBikeServiceHistoryShow: (): AnalyticsEvent => ['garage_bike_service_history_show', {}],

  garageHelmetShow: (): AnalyticsEvent => ['garage_helmet_show', {}],
  garageHelmetAddStart: (): AnalyticsEvent => ['garage_helmet_add_start', {}],
  garageHelmetAddSuccess: (): AnalyticsEvent => ['garage_helmet_add_success', {}],
  garageHelmetEditShow: (): AnalyticsEvent => ['garage_helmet_edit_show', {}],
  garageHelmetEditSuccess: (): AnalyticsEvent => ['garage_helmet_edit_success', {}],
  garageHelmetDelete: (): AnalyticsEvent => ['garage_helmet_delete', {}],

  garageIntercomShow: (): AnalyticsEvent => ['garage_intercom_show', {}],
  garageIntercomAddStart: (): AnalyticsEvent => ['garage_intercom_add_start', {}],
  garageIntercomAddSuccess: (): AnalyticsEvent => ['garage_intercom_add_success', {}],
  garageIntercomEditShow: (): AnalyticsEvent => ['garage_intercom_edit_show', {}],
  garageIntercomEditSuccess: (): AnalyticsEvent => ['garage_intercom_edit_success', {}],
  garageIntercomDelete: (): AnalyticsEvent => ['garage_intercom_delete', {}],

  // roadbook
  roadbookShowTrips: (): AnalyticsEvent => ['roadbook_show_trips', {}],
  roadbookShowSections: (): AnalyticsEvent => ['roadbook_show_sections', {}],
  roadbookShowPlannedTrips: (): AnalyticsEvent => ['roadbook_show_planned_trips', {}],

  // section
  sectionDetailShow: (): AnalyticsEvent => ['section_detail_show', {}],
  sectionDelete: (): AnalyticsEvent => ['section_delete', {}],
  sectionCreateStart: (): AnalyticsEvent => ['section_create_start', {}],
  sectionCreateRouteSelect: (): AnalyticsEvent => ['section_create_route_select', {}],
  sectionCreateCancel: (step: string): AnalyticsEvent => ['section_create_cancel', { step }],
  sectionCreateSave: (numberTags: number, numberPhotos: number): AnalyticsEvent => [
    'section_create_save',
    { number_tags: numberTags, number_photos: numberPhotos }
  ],
  sectionEditShow: (): AnalyticsEvent => ['section_edit_show', {}],
  sectionComment: (source: string): AnalyticsEvent => ['section_comment', { source }],
  sectionLike: (source: string): AnalyticsEvent => ['section_like', { source }],
  sectionFollow: (): AnalyticsEvent => ['section_follow', {}],
  sectionUnfollow: (): AnalyticsEvent => ['section_unfollow', {}],

  // challenges
  challengeOverviewShow: (): AnalyticsEvent => ['challenge_overview_show', {}],
  challengeDetailShow: (): AnalyticsEvent => ['challenge_detail_show', {}],
  challengeJoin: (): AnalyticsEvent => ['challenge_join', {}],
  challengeLeave: (): AnalyticsEvent => ['challenge_leave', {}],

  // packride
  packrideShow: (source: string): AnalyticsEvent => ['packride_show', { source }],
  packrideCreate: (): AnalyticsEvent => ['packride_create', {}],
  packrideJoin: (numberMembers: number, source: string): AnalyticsEvent => [
    'packride_join',
    { number_members: numberMembers, source }
  ],
  packrideEnd: (): AnalyticsEvent => ['packride_end', {}],
  packrideMessageSend: (messageType: string): AnalyticsEvent => [
    'packride_message_send',
    { message_type: messageType }
  ],
  packrideNearbySuggestionShow: (): AnalyticsEvent => ['packride_nearby_suggestion_show', {}],

  // live location
  liveLocationWebViewShow: (): AnalyticsEvent => ['live_tracking_web_view_show', {}],

  // navigation
  navigationPlanningShow: (): AnalyticsEvent => ['navigation_planning_show', {}],
  navigationPlanningSuccess: (
    isRoundtrip: boolean,
    distance: number,
    duration: number,
    profile: string,
    numberOfWaypoints: number
  ): AnalyticsEvent => [
    'navigation_planning_success',
    {
      is_roundtrip: isRoundtrip,
      distance,
      duration,
      profile,
      number_of_waypoints: numberOfWaypoints
    }
  ],
  navigationPlanningCancel: (): AnalyticsEvent => ['navigation_planning_cancel', {}],
  navigationPlanningRouteSave: (): AnalyticsEvent => ['navigation_planning_route_save', {}],

  // offline maps
  offlineMapOverviewShow: (): AnalyticsEvent => ['offline_map_overview_show', {}],
  offlineMapCreateShow: (): AnalyticsEvent => ['offline_map_create_show', {}],
  offlineMapCreateSuccess: (area: string): AnalyticsEvent => [
    'offline_map_create_success',
    { area }
  ],
  offlineMapCreateCancel: (): AnalyticsEvent => ['offline_map_create_cancel', {}],
  offlineMapDetailShow: (): AnalyticsEvent => ['offline_map_detail_show', {}],
  offlineMapDelete: (): AnalyticsEvent => ['offline_map_delete', {}],

  // groups
  groupOverviewShow: (): AnalyticsEvent => ['group_overview_show', {}],
  groupDetailShow: (): AnalyticsEvent => ['group_detail_show', {}],
  groupCreateShow: (): AnalyticsEvent => ['group_create_show', {}],
  groupCreateSuccess: (visibility: string): AnalyticsEvent => [
    'group_create_success',
    { visibility }
  ],
  groupCreateCancel: (step: string): AnalyticsEvent => ['group_create_cancel', { step }],
  groupBrowseShow: (): AnalyticsEvent => ['group_browse_show', {}],
  groupSearchShow: (): AnalyticsEvent => ['group_search_show', {}],
  groupJoin: (): AnalyticsEvent => ['group_join', {}],
  groupLeave: (): AnalyticsEvent => ['group_leave', {}],
  groupRequestAccess: (): AnalyticsEvent => ['group_request_access', {}],
  groupInviteAccept: (): AnalyticsEvent => ['group_invite_accept', {}],
  groupInviteDecline: (): AnalyticsEvent => ['group_invite_decline', {}],
  groupShare: (): AnalyticsEvent => ['group_share', {}],
  groupEditShow: (): AnalyticsEvent => ['group_edit_show', {}],
  groupDelete: (): AnalyticsEvent => ['group_delete', {}],
  groupAllMembersShow: (): AnalyticsEvent => ['group_all_members_show', {}],
  groupManageRequestsShow: (): AnalyticsEvent => ['group_manage_requests_show', {}],
  groupManageRequestAccept: (): AnalyticsEvent => ['group_manage_request_accept', {}],
  groupManageRequestDecline: (): AnalyticsEvent => ['group_manage_request_decline', {}],

  // getaways
  getawayOverviewShow: (): AnalyticsEvent => ['getaway_overview_show', {}],
  getawayDetailShow: (): AnalyticsEvent => ['getaway_detail_show', {}],
  getawayDetailCommentsShow: (): AnalyticsEvent => ['getaway_detail_comments_show', {}],
  getawayCreateShow: (): AnalyticsEvent => ['getaway_create_show', {}],
  getawayCreateSuccess: (style: string, duration: string, visibility: string): AnalyticsEvent => [
    'getaway_create_success',
    { style, duration, visibility }
  ],
  getawayCreateCancel: (step: string): AnalyticsEvent => ['getaway_create_cancel', { step }],
  getawayBrowseShow: (): AnalyticsEvent => ['getaway_browse_show', {}],
  getawayParticipate: (): AnalyticsEvent => ['getaway_participate', {}],
  getawayLeave: (): AnalyticsEvent => ['getaway_leave', {}],
  getawayShare: (): AnalyticsEvent => ['getaway_share', {}],
  getawayEditShow: (): AnalyticsEvent => ['getaway_edit_show', {}],
  getawayDelete: (): AnalyticsEvent => ['getaway_delete', {}],
  getawayComment: (): AnalyticsEvent => ['getaway_comment', {}],
  getawayInviteShow: (): AnalyticsEvent => ['getaway_invite_show', {}],

  // settings
  settingNavigationAudioShow: (): AnalyticsEvent => ['setting_navigation_audio_show', {}],
  settingNavigationAudioChange: (): AnalyticsEvent => ['setting_navigation_audio_change', {}],
  settingAutoPauseChange: (enabled: boolean): AnalyticsEvent => [
    'setting_auto_pause_change',
    { enabled }
  ],
  settingUnlockScreenChange: (enabled: boolean): AnalyticsEvent => [
    'setting_unlock_screen_change',
    { enabled }
  ],
  settingMapTypeChange: (map: string, source: string): AnalyticsEvent => [
    'setting_map_type_change',
    { map, source }
  ],
  settingUnitChange: (unit: string): AnalyticsEvent => ['setting_unit_change', { unit }],
  settingNavigationUnitChange: (): AnalyticsEvent => ['setting_navigation_unit_change', {}],
  settingAboutShow: (): AnalyticsEvent => ['setting_about_show', {}],
  sendLogs: (manual: boolean): AnalyticsEvent => ['send_logs', { manual }],

  // general screen views
  feedShow: (): AnalyticsEvent => ['feed_show', {}],
  goShow: (): AnalyticsEvent => ['go_show', {}],
  notificationOverviewShow: (): AnalyticsEvent => ['notification_overview_show', {}],
  photosShow: (): AnalyticsEvent => ['photos_show', {}],
  moreShow: (): AnalyticsEvent => ['more_show', {}],
  feedbackShow: (): AnalyticsEvent => ['feedback_show', {}],
  blogPostShow: (): AnalyticsEvent => ['blog_post_show', {}],
  supportShow: (): AnalyticsEvent => ['support_show', {}],

  // rating
  ratingShow: (): AnalyticsEvent => ['rating_show', {}],
  ratingCancel: (step: string): AnalyticsEvent => ['rating_cancel', { step }],
  ratingAnswer: (rating: string): AnalyticsEvent => ['rating_answer', { rating }],
  ratingStoreRating: (): AnalyticsEvent => ['rating_store_rating', {}],
  ratingProvideFeedback: (): AnalyticsEvent => ['rating_provide_feedback', {}],

  // profile
  profileShow: (): AnalyticsEvent => ['profile_show', {}],
  profileSeasonGoalSet: (
    source: string,
    distanceGoal: number,
    duration: number,
    unit: string
  ): AnalyticsEvent => [
    'profile_season_goal_set',
    { source, distance_goal: distanceGoal, duration, unit }
  ],
  profileSocialLinkOpen: (platform: string): AnalyticsEvent => [
    'profile_social_link_open',
    { platform }
  ],

  // voucher
  voucherShow: (source: string): AnalyticsEvent => ['voucher_show', { source }],
  voucherRedeem: (source: string): AnalyticsEvent => ['voucher_redeem', { source }],
  voucherFail: (source: string, reason: string): AnalyticsEvent => [
    'voucher_fail',
    { source, reason }
  ],

  // deep link
  deepLinkOpen: (
    type: string,
    campaignName: string,
    campaignSource: string,
    campaignKeyword: string
  ): AnalyticsEvent => [
    'deep_link_open',
    {
      type,
      campaign_name: campaignName,
      campaign_source: campaignSource,
      campaign_keyword: campaignKeyword
    }
  ],

  // posting
  postingCreateShow: (): AnalyticsEvent => ['posting_create_show', {}],
  postingCreateSuccess: (withText: boolean, withPhoto: boolean): AnalyticsEvent => [
    'posting_create_success',
    { with_text: withText, with_photo: withPhoto }
  ],
  postingCreateFail: (): AnalyticsEvent => ['posting_create_fail', {}],
  postingDetailShow: (): AnalyticsEvent => ['posting_detail_show', {}],
  postingLike: (): AnalyticsEvent => ['posting_like', {}],
  postingDislike: (): AnalyticsEvent => ['posting_dislike', {}],
  postingCommentSuccess: (): AnalyticsEvent => ['posting_comment_success', {}],
  postingCommentFail: (): AnalyticsEvent => ['posting_comment_fail', {}],
  postingDelete: (): AnalyticsEvent => ['posting_delete', {}],
  postingCommentDelete: (): AnalyticsEvent => ['posting_comment_delete', {}],
  postingReportOpen: (isComment: boolean): AnalyticsEvent => [
    'posting_report_open',
    { is_comment: isComment }
  ],
  postingReportFinish: (isComment: boolean, type: string): AnalyticsEvent => [
    'posting_report_finish',
    { is_comment: isComment, type }
  ],
  postingCommentLike: (): AnalyticsEvent => ['posting_comment_like', {}],
  postingCommentDislike: (): AnalyticsEvent => ['posting_comment_dislike', {}],

  // follow

  followOverviewFollowerShow: (loggedInUser: boolean): AnalyticsEvent => [
    'follow_overview_follower_show',
    { logged_in_user: loggedInUser }
  ],
  followOverviewFollowingShow: (loggedInUser: boolean): AnalyticsEvent => [
    'follow_overview_following_show',
    { logged_in_user: loggedInUser }
  ],
  followOverviewRequestsShow: (): AnalyticsEvent => ['follow_overview_requests_show', {}],
  followOverviewBlockedShow: (): AnalyticsEvent => ['follow_overview_blocked_show', {}],
  followFollow: (): AnalyticsEvent => ['follow_follow', {}],
  followUnfollow: (): AnalyticsEvent => ['follow_unfollow', {}],
  followBlock: (): AnalyticsEvent => ['follow_block', {}],
  followUnblock: (): AnalyticsEvent => ['follow_unblock', {}],
  followRequestAccept: (): AnalyticsEvent => ['follow_request_accept', {}],
  followRequestDecline: (): AnalyticsEvent => ['follow_request_decline', {}],
  followRemove: (): AnalyticsEvent => ['follow_remove', {}],
  followIntroShow: (): AnalyticsEvent => ['follow_intro_show', {}],
  followIntroFinish: (autoFollow: boolean): AnalyticsEvent => [
    'follow_intro_finish',
    { auto_follow: autoFollow }
  ],
  followSearchShow: (): AnalyticsEvent => ['friends_search_show', {}],

  // statistics
  statisticsOpen: (): AnalyticsEvent => ['statistics_open', {}],
  statisticsAlltimeOpen: (): AnalyticsEvent => ['statistics_alltime_open', {}],
  statisticsYearOpen: (): AnalyticsEvent => ['statistics_year_open', {}],
  statisticsMonthOpen: (): AnalyticsEvent => ['statistics_month_open', {}],
  statisticsWeekOpen: (): AnalyticsEvent => ['statistics_week_open', {}],

  // bonus club
  bonusClubOverviewShow: (): AnalyticsEvent => ['bonus_club_overview_show', {}],
  bonusClubDetailOpen: (offerId: string): AnalyticsEvent => [
    'bonus_club_detail_open',
    { offer_id: offerId }
  ],
  bonusClubCodeCopy: (offerId: string): AnalyticsEvent => [
    'bonus_club_code_copy',
    { offer_id: offerId }
  ],
  bonusClubOpenShop: (offerId: string): AnalyticsEvent => [
    'bonus_club_open_shop',
    { offer_id: offerId }
  ],

  // referral
  referralShow: (): AnalyticsEvent => ['referral_show', {}],
  referralCopyCode: (): AnalyticsEvent => ['referral_copy_code', {}],
  referralShareCode: (): AnalyticsEvent => ['referral_share_code', {}],

  // session cookie migration (TRANSITIONAL — remove together with
  // migrateLegacySessionCookie in plugins/01.store.js once the legacy cookie
  // has aged out of the active user base)
  sessionCookieMigrated: (migratedSession: boolean): AnalyticsEvent => [
    'session_cookie_migrated',
    { migrated_session: migratedSession }
  ]
}
